/* ==========================================================================
   Free shipping progress bar
   ==========================================================================

   Two jobs:

   1. Resolve the threshold for currencies Liquid could not resolve on the
      server. Liquid has no access to the storefront conversion rate, so for
      SEK, DKK, PLN, CZK, HUF and RON the threshold is worked out here from
      window.Shopify.currency.rate, which is the shop-currency-to-buyer-
      currency rate Shopify itself uses.

   2. Keep the bar current on the cart page. The cart drawer re-renders its
      whole inner element on every cart change, so Liquid handles it there
      and this element is simply rebuilt. The cart page is not re-rendered
      by Dawn, so there we listen for cart updates and recompute.

   Money is formatted from a probe string rendered by Liquid's money filter,
   so the output matches the store's own currency format rather than
   guessing at one.
   ========================================================================== */

(function () {
  if (customElements.get('free-shipping-bar')) return;

  // Built from char codes so the source stays pure ASCII and survives any
  // copy through tooling that mangles escapes or non-breaking spaces.
  var NBSP = String.fromCharCode(160); // non-breaking space
  var NNBSP = String.fromCharCode(8239); // narrow non-breaking space
  var RSQUO = String.fromCharCode(8217); // right single quote, used by de-CH
  var MONEY_RE = new RegExp(
    "\\d[\\d.,'" + RSQUO + "\\s" + NBSP + NNBSP + "]*\\d|\\d"
  );

  /**
   * Work out prefix, suffix, separators and decimal places from a rendered
   * money string whose value is known to be 1111111.11.
   */
  function parseMoneyFormat(probe) {
    if (!probe) return null;

    var s = String(probe)
      .replace(/&nbsp;|&#160;|&#xa0;/gi, NBSP)
      .replace(/&amp;/gi, '&');

    var match = s.match(MONEY_RE);
    if (!match) return null;

    var num = match[0];
    var prefix = s.slice(0, match.index);
    var suffix = s.slice(match.index + num.length);

    var groups = num.split(/\D+/).filter(Boolean);
    var seps = num.split(/\d+/).filter(Boolean);

    var decimals = 0;
    var decimalSep = '.';
    var thousandsSep = '';

    if (groups.length > 1 && groups[groups.length - 1].length === 2) {
      decimals = 2;
      decimalSep = seps[seps.length - 1];
      thousandsSep = seps.length > 1 ? seps[0] : '';
    } else {
      decimals = 0;
      thousandsSep = seps.length ? seps[0] : '';
    }

    return {
      prefix: prefix,
      suffix: suffix,
      decimals: decimals,
      decimalSep: decimalSep,
      thousandsSep: thousandsSep
    };
  }

  function formatMoney(cents, format) {
    if (!format) return '';

    var units = cents / 100;
    var intPart;
    var decPart = '';

    if (format.decimals === 0) {
      intPart = String(Math.round(units));
    } else {
      var fixed = units.toFixed(2).split('.');
      intPart = fixed[0];
      decPart = fixed[1];
    }

    if (format.thousandsSep) {
      intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousandsSep);
    }

    return (
      format.prefix +
      intPart +
      (format.decimals ? format.decimalSep + decPart : '') +
      format.suffix
    );
  }

  /**
   * Converted thresholds always round UP, so the bar can never promise free
   * shipping that checkout will refuse.
   */
  function roundUp(cents, mode) {
    if (mode === 'none') return cents;

    var step;

    if (!mode || mode === 'auto') {
      var units = cents / 100;
      if (units < 100) step = 1;
      else if (units < 1000) step = 10;
      else if (units < 10000) step = 50;
      else if (units < 100000) step = 500;
      else step = 5000;
    } else {
      step = parseFloat(mode);
      if (!isFinite(step) || step <= 0) return cents;
    }

    var stepCents = Math.round(step * 100);
    return Math.ceil(cents / stepCents) * stepCents;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  var CHECK_ICON =
    '<span class="free-shipping-bar__icon" aria-hidden="true">' +
    '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M2.5 8.5 6 12l7.5-8" stroke="currentColor" stroke-width="1.75" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg></span>';

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.messageEl = this.querySelector('[data-fsb-message]');
      this.fillEl = this.querySelector('[data-fsb-fill]');
      this.trackEl = this.querySelector('[data-fsb-track]');

      this.format = parseMoneyFormat(this.dataset.moneyProbe);
      this.showWhenQualified = this.dataset.showWhenQualified !== 'false';
      this.threshold = this.resolveThreshold();

      if (!this.threshold || !this.format) {
        // Nothing we can say truthfully, so say nothing.
        this.remove();
        return;
      }

      // Liquid owns the copy only when it had both a threshold and a cart.
      // Otherwise this element takes over from here.
      if (this.dataset.serverRendered !== 'true') {
        this.update(parseInt(this.dataset.totalCents, 10) || 0);
      }

      this.classList.remove('free-shipping-bar--pending');

      // The drawer re-renders itself from Liquid on every cart change, so it
      // needs no listener. The cart page does.
      if (!this.closest('cart-drawer')) this.listenForCartUpdates();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    resolveThreshold() {
      var explicit = parseInt(this.dataset.thresholdCents, 10);
      if (explicit > 0) return explicit;

      var shopCents = parseInt(this.dataset.shopThresholdCents, 10);
      if (!(shopCents > 0)) return 0;

      var rate = parseFloat(
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || ''
      );
      if (!isFinite(rate) || rate <= 0) return 0;

      return roundUp(Math.ceil(shopCents * rate), this.dataset.rounding);
    }

    update(totalCents) {
      // An empty cart gets no progress bar. This is also how the element
      // waits, hidden, inside the add-to-cart notification until the first
      // item is added.
      if (!(totalCents > 0)) {
        this.hidden = true;
        return;
      }

      var remaining = this.threshold - totalCents;
      var qualified = remaining <= 0;

      if (qualified && !this.showWhenQualified) {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      this.classList.toggle('free-shipping-bar--qualified', qualified);

      if (this.messageEl) {
        if (qualified) {
          this.messageEl.innerHTML = CHECK_ICON + escapeHtml(this.dataset.msgQualified || '');
        } else {
          var display =
            this.format.decimals === 0
              ? Math.ceil(remaining / 100) * 100
              : Math.ceil(remaining);

          this.messageEl.innerHTML = escapeHtml(this.dataset.msgRemaining || '').replace(
            '[amount]',
            '<strong>' + escapeHtml(formatMoney(display, this.format)) + '</strong>'
          );
        }
      }

      var percent = qualified
        ? 100
        : Math.max(0, Math.min(100, Math.round((totalCents / this.threshold) * 100)));

      if (this.fillEl) this.fillEl.style.width = percent + '%';
      if (this.trackEl) this.trackEl.setAttribute('aria-valuenow', String(percent));
    }

    listenForCartUpdates() {
      var self = this;

      var refresh = function () {
        var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .then(function (cart) {
            if (!cart) return;
            if (!cart.item_count) {
              self.hidden = true;
              return;
            }
            self.update(cart.total_price);
          })
          .catch(function () {
            /* leave the last known good state on screen */
          });
      };

      // Dawn declares `subscribe` as a function (so it is on window) but
      // PUB_SUB_EVENTS with `const`, which is NOT a window property. Both have
      // to be reached as bare identifiers behind a typeof guard.
      var sub = typeof subscribe === 'function' ? subscribe : null;
      var eventName =
        typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate
          ? PUB_SUB_EVENTS.cartUpdate
          : 'cart-update';

      if (sub) {
        this.unsubscribe = sub(eventName, refresh);
      } else {
        document.addEventListener('cart:refresh', refresh);
        this.unsubscribe = function () {
          document.removeEventListener('cart:refresh', refresh);
        };
      }
    }
  }

  customElements.define('free-shipping-bar', FreeShippingBar);
})();